import { getCookie, setCookie } from '../utils/browser.util';
import { type StandaloneUserType, USER_TYPE_SWITCH_CONST, USER_TYPES } from '../constants/userTypeSwitch.const';
import { StandaloneEnvFlagsService } from './standaloneEnvFlags.service';

export interface UserTypeToggleDetail {
  readonly userType: StandaloneUserType;
}

export interface UserTypeTogglePayload {
  readonly organizationName: string;
}

export type { StandaloneUserType };

export abstract class UserTypeSwitchService {
  private static getOrganizationCookieConfig(type: StandaloneUserType): {
    cookieName: string;
    fallback: string;
  } {
    switch (type) {
      case USER_TYPES.tenant:
        return {
          cookieName: USER_TYPE_SWITCH_CONST.cookies.tenantOrgName,
          fallback: USER_TYPE_SWITCH_CONST.defaults.tenantOrgName,
        };
      case USER_TYPES.developer:
        return {
          cookieName: USER_TYPE_SWITCH_CONST.cookies.developerOrgName,
          fallback: USER_TYPE_SWITCH_CONST.defaults.developerOrgName,
        };
      default:
        return {
          cookieName: USER_TYPE_SWITCH_CONST.cookies.customerOrgName,
          fallback: USER_TYPE_SWITCH_CONST.defaults.customerOrgName,
        };
    }
  }

  private static isBooleanString(value: string | undefined): value is 'true' | 'false' {
    return value === 'true' || value === 'false';
  }

  private static syncApiSdkMocksEnabledFromCookieToStorage(): void {
    try {
      const currentStorageValue = localStorage.getItem(USER_TYPE_SWITCH_CONST.storage.apiSdkMocksEnabled);
      if (currentStorageValue !== null) {
        return;
      }

      const cookieValue = getCookie(USER_TYPE_SWITCH_CONST.cookies.apiSdkMocksEnabled);
      if (typeof cookieValue === 'string') {
        localStorage.setItem(USER_TYPE_SWITCH_CONST.storage.apiSdkMocksEnabled, cookieValue);
      }
    } catch {
      // Ignore storage access failures.
    }
  }

  private static syncApiSdkMocksEnabledFromStorageToCookie(): void {
    try {
      const value = localStorage.getItem(USER_TYPE_SWITCH_CONST.storage.apiSdkMocksEnabled);
      if (value !== null) {
        setCookie(USER_TYPE_SWITCH_CONST.cookies.apiSdkMocksEnabled, value);
      }
    } catch {
      // Ignore storage access failures.
    }
  }

  static ensureDefaults(): void {
    if (!getCookie(USER_TYPE_SWITCH_CONST.cookies.tenantOrgName)) {
      setCookie(USER_TYPE_SWITCH_CONST.cookies.tenantOrgName, USER_TYPE_SWITCH_CONST.defaults.tenantOrgName);
    }

    if (!getCookie(USER_TYPE_SWITCH_CONST.cookies.customerOrgName)) {
      setCookie(USER_TYPE_SWITCH_CONST.cookies.customerOrgName, USER_TYPE_SWITCH_CONST.defaults.customerOrgName);
    }

    if (!getCookie(USER_TYPE_SWITCH_CONST.cookies.developerOrgName)) {
      setCookie(USER_TYPE_SWITCH_CONST.cookies.developerOrgName, USER_TYPE_SWITCH_CONST.defaults.developerOrgName);
    }

    if (!getCookie(USER_TYPE_SWITCH_CONST.cookies.userTypeSwitch)) {
      setCookie(USER_TYPE_SWITCH_CONST.cookies.userTypeSwitch, USER_TYPE_SWITCH_CONST.defaults.userType);
    }

    if (!getCookie(USER_TYPE_SWITCH_CONST.cookies.apiSdkMocksEnabled)) {
      setCookie(USER_TYPE_SWITCH_CONST.cookies.apiSdkMocksEnabled, USER_TYPE_SWITCH_CONST.defaults.apiSdkMocksEnabled);
    }

    this.syncApiSdkMocksEnabledFromCookieToStorage();
  }

  static getCurrentUserType(): StandaloneUserType {
    const cookieValue = getCookie(USER_TYPE_SWITCH_CONST.cookies.userTypeSwitch);

    return Object.values(USER_TYPES).includes(cookieValue as StandaloneUserType)
      ? (cookieValue as StandaloneUserType)
      : USER_TYPE_SWITCH_CONST.defaults.userType;
  }

  static isCustomerUser(): boolean {
    return this.getCurrentUserType() === USER_TYPES.customer;
  }

  static isTenantUser(): boolean {
    return this.getCurrentUserType() === USER_TYPES.tenant;
  }

  static isDeveloperUser(): boolean {
    return this.getCurrentUserType() === USER_TYPES.developer;
  }

  static setUserType(enabledCustomerMode: boolean): void {
    setCookie(
      USER_TYPE_SWITCH_CONST.cookies.userTypeSwitch,
      enabledCustomerMode ? USER_TYPES.customer : USER_TYPES.tenant,
    );
  }

  static setUserTypeDirectly(userType: StandaloneUserType): void {
    setCookie(USER_TYPE_SWITCH_CONST.cookies.userTypeSwitch, userType);
  }

  static getOrganizationNameForMode(type: StandaloneUserType): string {
    const { cookieName, fallback } = this.getOrganizationCookieConfig(type);
    return getCookie(cookieName) || fallback;
  }

  static setOrganizationNameForMode(enabledCustomerMode: boolean, organizationName: string): void {
    const cookieName = enabledCustomerMode
      ? USER_TYPE_SWITCH_CONST.cookies.customerOrgName
      : USER_TYPE_SWITCH_CONST.cookies.tenantOrgName;
    setCookie(cookieName, organizationName);
  }

  static setOrganizationNameForModeDirectly(cookieName: string, organizationName: string): void {
    setCookie(cookieName, organizationName);
  }

  static setOrganizationNameForUserType(userType: StandaloneUserType, organizationName: string): void {
    const { cookieName } = this.getOrganizationCookieConfig(userType);
    this.setOrganizationNameForModeDirectly(cookieName, organizationName);
  }

  static resolveClientId(defaultClientId: string, tenantClientId?: string, developerClientId?: string): string {
    if (this.isTenantUser()) {
      return tenantClientId ?? defaultClientId;
    } else if (this.isDeveloperUser()) {
      return developerClientId ?? defaultClientId;
    }
    return defaultClientId;
  }

  static disableApiSdkMocksInStandalone(): void {
    this.setApiSdkMocksEnabled(false);
  }

  static isApiSdkMocksEnabled(): boolean {
    try {
      const storageValue = localStorage.getItem(USER_TYPE_SWITCH_CONST.storage.apiSdkMocksEnabled) ?? undefined;
      if (this.isBooleanString(storageValue)) {
        return storageValue === 'true';
      }

      const cookieValue = getCookie(USER_TYPE_SWITCH_CONST.cookies.apiSdkMocksEnabled);
      if (this.isBooleanString(cookieValue)) {
        return cookieValue === 'true';
      }
    } catch {
      // Ignore storage access failures.
    }

    return USER_TYPE_SWITCH_CONST.defaults.apiSdkMocksEnabled === 'true';
  }

  static setApiSdkMocksEnabled(enabled: boolean): void {
    const serializedValue = enabled ? 'true' : 'false';
    setCookie(USER_TYPE_SWITCH_CONST.cookies.apiSdkMocksEnabled, serializedValue);

    try {
      localStorage.setItem(USER_TYPE_SWITCH_CONST.storage.apiSdkMocksEnabled, serializedValue);
    } catch {
      // Ignore storage access failures.
    }
  }

  static toggleApiSdkMocksEnabled(): boolean {
    const nextEnabled = !this.isApiSdkMocksEnabled();
    this.setApiSdkMocksEnabled(nextEnabled);
    return nextEnabled;
  }

  static dispatchToggleEventForUserType(userType: StandaloneUserType): void {
    StandaloneEnvFlagsService.resetAllOverrides();
    window.dispatchEvent(
      new CustomEvent<UserTypeToggleDetail>(USER_TYPE_SWITCH_CONST.events.userTypeToggle, { detail: { userType } }),
    );
  }

  static registerToggleListener(listener: (payload: UserTypeTogglePayload) => void): void {
    window.addEventListener(USER_TYPE_SWITCH_CONST.events.userTypeToggle, (event: Event) => {
      this.syncApiSdkMocksEnabledFromStorageToCookie();
      const { userType } = (event as CustomEvent<UserTypeToggleDetail>).detail ?? { userType: USER_TYPES.customer };
      listener({ organizationName: this.getOrganizationNameForMode(userType) });
    });
  }
}
